import { kbInboxPath } from "@/lib/kb-routes";

export type KnowledgeEntryFieldMeta = {
  key: string;
  label: string;
};

export type KnowledgeEntryContext = {
  mode: "knowledge_entry";
  pageId: number;
  wikiId: number;
  pageSlug?: string;
  accountId: string;
  /** When set, chips link into brand content stack (H1 context). */
  brandId?: string | null;
  structured: Record<string, unknown>;
  fields: KnowledgeEntryFieldMeta[];
};

export type KnowledgeEntryChip = {
  id: string;
  label: string;
  /** When set, clicking sends this prompt to Hermes */
  prompt?: string;
  /** When set, clicking navigates (SPA path) */
  href?: string;
};

export type KbAdaptiveSuggestionInput = {
  accountId: string;
  brandId?: string | null;
  fields: KnowledgeEntryFieldMeta[];
  structured: Record<string, unknown>;
};

function hasValue(value: unknown): boolean {
  if (value == null) return false;
  if (typeof value === "string") return value.trim().length > 0;
  if (Array.isArray(value)) return value.length > 0;
  return true;
}

/** M3.5b adaptive chips from enterprise/brand seed fill rate (shared by knowledge-entry + empty-state). */
export function buildKbAdaptiveSuggestions(input: KbAdaptiveSuggestionInput): KnowledgeEntryChip[] {
  const filled = input.fields.filter((field) => hasValue(input.structured[field.key]));
  const empty = input.fields.filter((field) => !hasValue(input.structured[field.key]));
  const fillRate = input.fields.length === 0 ? 0 : filled.length / input.fields.length;
  const inboxHref = kbInboxPath({
    accountId: input.accountId,
    brandId: input.brandId,
  });

  if (fillRate === 0) {
    return [
      {
        id: "intro-basics",
        label: "帮我整理企业基本信息",
        prompt: "帮我录入企业画像要点。请先用访谈方式问我公司全称、简称和主营业务。",
      },
      {
        id: "interview-business",
        label: "帮我补充公司主营业务",
        prompt: "帮我补充公司主营业务。请以访谈方式问我：公司做什么、服务谁、核心产品或服务是什么？",
      },
      {
        id: "open-inbox",
        label: "去知识旅程上传素材",
        href: inboxHref,
      },
      {
        id: "brand-ask",
        label: "根据当前素材帮我创建品牌",
        prompt:
          "根据当前素材帮我创建品牌。如果素材里出现了品牌名称，请列出候选品牌，经我确认后再在当前账户下创建品牌档案；不要静默创建。",
      },
    ];
  }

  if (fillRate < 1) {
    const missingLabels = empty.slice(0, 5).map((field) => field.label).join("、");
    return [
      {
        id: "whats-missing",
        label: "帮我看看还缺哪些信息",
        prompt: `帮我看看企业画像还缺哪些信息。当前还缺：${missingLabels || "若干字段"}。请逐项向我提问补充。`,
      },
      {
        id: "fill-contact",
        label: "从官网补充联系方式",
        prompt:
          "请用 websearch 按企业全称检索公开官网与联系电话，再用 webfetch 打开候选页核实后，用提案写入 website / contact_phone；不要编造；工具无可靠结果时再问我。",
      },
      {
        id: "open-inbox-partial",
        label: "打开知识旅程",
        href: inboxHref,
      },
      {
        id: "brand-ask-partial",
        label: "用这些素材创建一个品牌",
        prompt:
          "根据刚才对话或素材帮我创建品牌档案。如有品牌线索请列出候选，经我确认后再创建。",
      },
    ];
  }

  return [
    {
      id: "review-update",
      label: "核对并更新已有字段",
      prompt: "请核对当前企业画像各字段是否准确，指出可改进处；需要写入时用提案让我确认。",
    },
    {
      id: "brand-ask-full",
      label: "根据素材帮我创建品牌",
      prompt:
        "根据素材或对话中的品牌线索帮我创建品牌档案；列出候选后经我确认再创建，禁止静默创建。",
    },
    {
      id: "open-inbox-full",
      label: "打开知识旅程",
      href: inboxHref,
    },
  ];
}

export function buildKnowledgeEntryChips(ctx: KnowledgeEntryContext): KnowledgeEntryChip[] {
  return buildKbAdaptiveSuggestions({
    accountId: ctx.accountId,
    brandId: ctx.brandId,
    fields: ctx.fields,
    structured: ctx.structured,
  });
}

export function knowledgeEntryChatContext(ctx: KnowledgeEntryContext) {
  return {
    mode: "knowledge_entry" as const,
    page_id: ctx.pageId,
    wiki_id: ctx.wikiId,
    page_slug: ctx.pageSlug,
    kb_bindings: [{ wiki_id: ctx.wikiId, access: "write" as const }],
  };
}

export const HERMES_WRITE_ACCEPTED_EVENT = "hermes:write-accepted";

export type HermesWriteAcceptedDetail = {
  action: string;
  payload?: Record<string, unknown> | null;
  /** 旅程手册写入后回填的 id；非 journey 提案可省略 */
  journey_id?: number | null;
  brand_id?: number | null;
};

/** 会改变 Wiki 页面列表 / 进度的写入动作（工作台侧栏可按 Wiki 条件刷新）。 */
const KB_TREE_REFRESH_ACTIONS = new Set([
  "seed_update",
  "page_upsert",
  "kb_vfs_write",
  "kb_vfs_edit",
  "kb_vfs_rm",
  "page_delete",
  "delete",
]);

function coercePositiveId(value: unknown): number | null {
  if (value == null || value === "") return null;
  const n = Number(value);
  return Number.isFinite(n) && n > 0 ? n : null;
}

/** 从 VFS path（如 wiki/4/pages/slug.md）解析 wiki_id。 */
export function wikiIdFromKbVfsPath(path: unknown): number | null {
  if (typeof path !== "string") return null;
  const match = path.trim().match(/^wiki\/(\d+)(?:\/|$)/i);
  return match ? coercePositiveId(match[1]) : null;
}

/** 确认写入事件中的目标 page_id（创建接受后应由服务端回填）。 */
export function hermesWriteAcceptedPageId(
  detail: HermesWriteAcceptedDetail,
): number | null {
  return coercePositiveId(detail.payload?.page_id);
}

/** 确认写入事件中的 wiki_id（payload.wiki_id，或从 path 回退解析）。 */
export function hermesWriteAcceptedWikiId(
  detail: HermesWriteAcceptedDetail,
): number | null {
  return (
    coercePositiveId(detail.payload?.wiki_id) ?? wikiIdFromKbVfsPath(detail.payload?.path)
  );
}

/**
 * 当前知识库工作台是否应刷新侧栏（WikiTree / 进度）。
 * - 仅当正在查看的 Wiki 与提案目标 Wiki 一致时刷新（用户在别的页面聊天则无监听或 wiki 不匹配 → 不刷）。
 * - seed_update 旧提案可能无 wiki_id：仅刷新当前已打开的库（兼容）。
 */
export function shouldRefreshKbWorkbenchTree(
  detail: HermesWriteAcceptedDetail,
  currentWikiId: number | null | undefined,
): boolean {
  if (currentWikiId == null || currentWikiId <= 0) return false;
  if (!KB_TREE_REFRESH_ACTIONS.has(detail.action)) return false;

  const wikiId = hermesWriteAcceptedWikiId(detail);
  if (wikiId == null) {
    return detail.action === "seed_update";
  }
  return wikiId === currentWikiId;
}

/**
 * 当前打开的 PageEditor 是否应拉取服务端最新页并同步本地 title/body/structured。
 * - seed_update：page_id 匹配，或未带 page_id（兼容旧提案）
 * - page_upsert / kb_vfs_write / kb_vfs_edit：必须 page_id 匹配当前页
 */
export function shouldRefreshOpenPageEditor(
  detail: HermesWriteAcceptedDetail,
  openPageId: number | null | undefined,
): boolean {
  if (openPageId == null) return false;
  if (detail.action === "seed_update") {
    const pageId = hermesWriteAcceptedPageId(detail);
    return pageId == null || pageId === openPageId;
  }
  if (
    detail.action === "page_upsert" ||
    detail.action === "kb_vfs_write" ||
    detail.action === "kb_vfs_edit"
  ) {
    return hermesWriteAcceptedPageId(detail) === openPageId;
  }
  return false;
}

/** 新建页面类提案接受后应选中新页（page_upsert / kb_vfs_write 且 existing === false）。 */
export function isHermesNewPageAccepted(detail: HermesWriteAcceptedDetail): boolean {
  if (detail.action !== "page_upsert" && detail.action !== "kb_vfs_write") return false;
  return detail.payload?.existing === false;
}

/** @deprecated 使用 isHermesNewPageAccepted */
export function isHermesPageUpsertCreate(detail: HermesWriteAcceptedDetail): boolean {
  return isHermesNewPageAccepted(detail);
}
